import React, { useMemo } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper } from "@mui/material";
import { Vessel } from "../types/Vessel";
import { Container } from "../types/Container";
import { useTranslation } from 'react-i18next';

interface StabilityTableProps {
  vessel: Vessel;
  containers: Container[];
  cogMaxLoad: any;
  cogLoaded?: number;
}

const RenderStabilityTable: React.FC<StabilityTableProps> = ({ vessel, containers, cogMaxLoad, cogLoaded }) => {
  const { t } = useTranslation();
  const numberOfLayers = vessel.positions.length;
  const vesselId = vessel["@id"].replace("/api/vessels/", "");
  const baseOffset = Number((vessel as any).baseOffset ?? 0);
  const emptyKg = Number((vessel as any).emptyCenterOfGravity?.z ?? 1);

  const stabilityData = useMemo(() => {
    let cumulativeHeight = baseOffset;
    const data = [];
    for (let layer = 0; layer <= numberOfLayers; layer++) {
      if (layer === 0) {
        const weightSum = (vessel as any).emptyWeightInTonnes ?? 1000;
        data.push({ layer, weightSum, factorKg: emptyKg, result: weightSum * emptyKg });
        continue;
      }
      const layerContainers = containers.filter(
        c => c.position?.[0] === layer && c.position[3] === vesselId
      );
      const weightSum = layerContainers.reduce((acc, c) => acc + (c.weight / 1000), 0);
      const heights = layerContainers.map(c => Number((c as any).containerType?.height ?? 2.89));
      const avgHeight = heights.length > 0 ? heights.reduce((a, b) => a + b, 0) / heights.length : 0;
      const factorKg = heights.length > 0 ? cumulativeHeight + avgHeight / 2 : 0;
      if (heights.length > 0) cumulativeHeight += avgHeight;
      data.push({ layer, weightSum, factorKg, result: weightSum * factorKg });
    }
    return data;
  }, [containers, numberOfLayers, baseOffset, emptyKg, vesselId, vessel]);

  if (numberOfLayers === 0 || containers.length === 0) {
    return null;
  }

  const totalWeight = stabilityData.reduce((sum, d) => sum + d.weightSum, 0);
  const totalVertMom = stabilityData.reduce((sum, d) => sum + d.result, 0);
  const vocog = totalWeight > 0 ? totalVertMom / totalWeight : 0;
  const shownKg = cogLoaded ?? vocog;

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("layer")}</TableCell>
            <TableCell align="right">{t("weightInTonnes")}</TableCell>
            <TableCell align="right">{t("kg")}</TableCell>
            <TableCell align="right">{t("verticalMoment")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...stabilityData].reverse().map(({ layer, weightSum, factorKg, result }) => (
            <TableRow key={layer}>
              <TableCell>{layer === 0 ? t("emptyVessel") : layer}</TableCell>
              <TableCell align="right">{weightSum.toFixed(2)}</TableCell>
              <TableCell align="right">{factorKg.toFixed(3)}</TableCell>
              <TableCell align="right">{result.toFixed(2)}</TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell rowSpan={2}><Typography fontWeight={700}>{t("total")}</Typography></TableCell>
            <TableCell align="right">
                <Typography fontWeight={700}>{totalWeight.toFixed(3)}</Typography>
            </TableCell>
            <TableCell/>
            <TableCell align="right">
                <Typography fontWeight={700}>{totalVertMom.toFixed(3)}</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell/>
            <TableCell align="right">
                <Typography fontWeight={700} color={shownKg > cogMaxLoad && cogMaxLoad !== 1 && shownKg !== 1 ? 'error' : 'inherit'}>{shownKg.toFixed(5)}</Typography>
            </TableCell>
            <TableCell/>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RenderStabilityTable;